<template>
  <div class="region-territories">
    <l-map
      v-if="mapData"
      ref="map"
      class="region-territories__map"
      :center="mapCenter"
      :zoom="12"
      :options="{ zoomControl: false, attributionControl: false }"
    >
      <l-control-zoom
        position="topleft"
        zoom-in-text="<i style='color: black;'>+</i>"
        zoom-out-text="<i style='color: black;'>-</i>"
      />
      <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <l-polygon
        :lat-lngs="mapData.polygon"
        :options="polygonOptions"
      >
        <l-tooltip :options="{ sticky: true }">
          {{ regionName || mapData.name }}
        </l-tooltip>
      </l-polygon>
      <vue2-leaflet-marker-cluster
        :options="optionsMarkerCluster"
        @clusterclick="onClusterClick"
      >
        <l-marker
          v-for="marker in mapMarkers"
          :key="marker.rowKey"
          :lat-lng="[marker.geo_lat, marker.geo_lon]"
        >
          <l-icon>
            <div class="marker">
              <v-icon :class="marker.iconClass">{{ marker.mdiIcon }}</v-icon>
            </div>
          </l-icon>
          <l-tooltip :options="{ sticky: true }">
            <div class="region-territories__marker-title">{{ marker.label }}</div>
            <div>{{ marker.name }}</div>
          </l-tooltip>
        </l-marker>
      </vue2-leaflet-marker-cluster>
    </l-map>

    <v-alert v-else type="info" outlined class="region-territories__empty ma-4">
      Карта территории для этого района пока не добавлена.
    </v-alert>

    <div v-if="$slots.controls" class="region-territories__controls">
      <slot name="controls" />
    </div>

    <div v-if="mapData && mapMarkers.length" class="region-territories__legend">
      <div v-for="item in legendItems" :key="item.type" class="region-territories__legend-item">
        <div class="marker region-territories__legend-marker">
          <v-icon small :class="item.iconClass">{{ item.mdiIcon }}</v-icon>
        </div>
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script>
import { divIcon, latLngBounds } from 'leaflet';
import { LMap, LControlZoom, LTileLayer, LPolygon, LTooltip, LMarker, LIcon } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
import { getRegionTerritories } from '@/components/areas/regionTerritories';

const MARKER_META = {
  doorphone: {
    label: 'Домофон',
    mdiIcon: 'mdi-map-marker',
    iconClass: 'action-icon',
  },
  gate: {
    label: 'Калитка',
    mdiIcon: 'mdi-gate-alert',
    iconClass: 'action-icon region-territories__icon-gate',
  },
  doorphone_gate: {
    label: 'Калитка',
    mdiIcon: 'mdi-gate-alert',
    iconClass: 'action-icon region-territories__icon-gate',
  },
  barrier: {
    label: 'Шлагбаум',
    mdiIcon: 'mdi-boom-gate',
    iconClass: 'action-icon region-territories__icon-barrier',
  },
  additional_entrance: {
    label: 'Доп. вход',
    mdiIcon: 'mdi-home-import-outline',
    iconClass: 'action-icon',
  },
};

export default {
  name: 'RegionTerritoriesMap',
  components: {
    LMap,
    LTileLayer,
    LPolygon,
    LTooltip,
    LControlZoom,
    LMarker,
    LIcon,
    Vue2LeafletMarkerCluster,
  },
  props: {
    regionId: {
      type: Number,
      default: null,
    },
    regionName: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    markers: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      polygonOptions: {
        color: '#d32f2f',
        weight: 2,
        fillColor: '#f8bbd0',
        fillOpacity: 0.4,
      },
      optionsMarkerCluster: {
        showCoverageOnHover: false,
        removeOutsideVisibleBounds: true,
        singleMarkerMode: false,
        zoomToBoundsOnClick: false,
        maxClusterRadius: 50,
        spiderLegPolylineOptions: {
          color: 'transparent',
        },
        iconCreateFunction: this.customIconCreateFunction,
      },
    };
  },
  computed: {
    mapData () {
      if (!this.regionId) return null;
      return getRegionTerritories(this.regionId);
    },
    mapCenter () {
      if (this.mapData && this.mapData.center) {
        return this.mapData.center;
      }
      if (!this.mapData || !this.mapData.polygon.length) {
        return [54.3, 48.4];
      }
      const points = this.mapData.polygon;
      const lat = points.reduce((sum, p) => sum + p[0], 0) / points.length;
      const lng = points.reduce((sum, p) => sum + p[1], 0) / points.length;
      return [lat, lng];
    },
    mapMarkers () {
      return (this.markers || [])
        .filter(item => this.hasCoordinates(item))
        .map(item => {
          const meta = MARKER_META[item.type] || {
            label: 'Устройство',
            mdiIcon: 'mdi-map-marker',
            iconClass: 'action-icon',
          };

          return {
            ...item,
            rowKey: `${item.type}-${item.id}`,
            label: meta.label,
            mdiIcon: meta.mdiIcon,
            iconClass: meta.iconClass,
          };
        });
    },
    legendItems () {
      const types = [...new Set(this.mapMarkers.map(marker => marker.type))];
      return types.map(type => {
        const meta = MARKER_META[type] || {
          label: type,
          mdiIcon: 'mdi-map-marker',
          iconClass: 'action-icon',
        };

        return {
          type,
          label: meta.label,
          mdiIcon: meta.mdiIcon,
          iconClass: meta.iconClass,
        };
      });
    },
  },
  watch: {
    active (value) {
      if (value) {
        this.$nextTick(() => {
          this.refreshMap();
        });
      }
    },
    regionId () {
      this.$nextTick(() => {
        this.refreshMap();
      });
    },
    markers: {
      deep: true,
      handler () {
        this.$nextTick(() => {
          this.refreshMap();
        });
      },
    },
  },
  mounted () {
    this.$nextTick(() => {
      this.refreshMap();
    });
  },
  methods: {
    hasCoordinates (item) {
      const lat = Number(item.geo_lat);
      const lon = Number(item.geo_lon);
      return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
    },
    refreshMap () {
      this.invalidateMapSize();
      this.fitMapBounds();
    },
    onClusterClick (event) {
      if (this.$refs.map && this.$refs.map.mapObject) {
        this.$refs.map.mapObject.fitBounds(event.layer.getBounds().pad(0.5));
      }
    },
    customIconCreateFunction (cluster) {
      const childCount = cluster._childCount;
      const firingCount = cluster._firingCount;

      let className = 'custom-cluster-icon';
      if (childCount < 10) {
        className += ' custom-cluster-icon-small';
      } else if (childCount < 100) {
        className += ' custom-cluster-icon-medium';
      } else {
        className += ' custom-cluster-icon-large';
      }
      if (firingCount && firingCount === 0) {
        className += ' custom-cluster-icon-none';
      }

      return divIcon({
        html: `<div class='${className}'><span>${childCount}</span></div>`,
        className,
      });
    },
    invalidateMapSize () {
      const map = this.$refs.map;
      if (map && map.mapObject) {
        map.mapObject.invalidateSize();
      }
    },
    fitMapBounds () {
      const map = this.$refs.map;
      if (!map || !map.mapObject || !this.mapData) return;

      const bounds = latLngBounds(this.mapData.polygon);
      this.mapMarkers.forEach((marker) => {
        bounds.extend([marker.geo_lat, marker.geo_lon]);
      });

      if (bounds.isValid()) {
        map.mapObject.fitBounds(bounds, { padding: [32, 32] });
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.region-territories {
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.region-territories__map {
  width: 100%;
  height: 100%;
  z-index: 0;
}

::v-deep .region-territories__map.leaflet-container {
  height: 100%;
}

.region-territories__empty {
  font-size: 1rem;
}

.region-territories__controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 500;
}

.region-territories__legend {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 500;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  font-size: 0.8125rem;
}

.region-territories__legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.region-territories__legend-marker {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.region-territories__marker-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.region-territories__icon-gate {
  padding-left: 3px;
}

.region-territories__icon-barrier {
  padding-left: 3px;
  padding-bottom: 3px;
}
</style>

<style lang="scss">
@import '../complexesMap/TheMap.scss';
</style>
